'use client';

import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { PlayIcon } from '@heroicons/react/20/solid';
import {
  ItemRelationsFragment,
  PriceVariantFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { ArrayElementType } from '@/integrations/crystallize/types/helpers/ArrayElementType';
import { FormatCurrency } from '@/ui/hooks/useCurrencyFormatter';
import Button from '../Button';
import { SharedButtonLinkProps } from './types';
import { CtaPrice, shouldShowCtaPrice } from './cta-price';
import { extractAnchorIdFromTile } from './utils/extract-anchor-id-from-tile';
import { buildLinkHref } from './utils/build-link-href';

interface LinkButtonProps extends SharedButtonLinkProps {
  internalLink?: string | null;
  externalLink?: string | null;
  fallbackLink?: string | null;
  destinationTopics?: { path: string }[] | null;
  anchorTile?: ArrayElementType<ItemRelationsFragment['items']>;
  country?: string | null;
  locale: string;
  openInNewTab?: boolean | null;
  addPlayIcon?: boolean | null;
  className?: string;
  urlParamsQueryString: string;
  pricePosition?: string | null;
  priceVariant?: PriceVariantFragment | null;
  applyClassesToButton?: boolean;
  formatCurrency: FormatCurrency;
}

export default function LinkButton({
  internalLink,
  externalLink,
  fallbackLink,
  destinationTopics,
  anchorTile,
  country,
  locale,
  openInNewTab,
  addPlayIcon,
  className,
  urlParamsQueryString,
  pricePosition,
  priceVariant,
  applyClassesToButton,
  formatCurrency,
  text,
  children,
  variant,
  noUppercase,
  ariaLabel,
  tileElementTrackingId,
  hasExternalAreaLink,
  onClick,
  modal,
}: Readonly<LinkButtonProps>) {
  const { href, isRestrictedFallback } = buildLinkHref({
    internalLink,
    externalLink,
    fallbackLink,
    destinationTopics,
    country,
    locale,
    urlParamsQueryString,
  });

  if (!href && !hasExternalAreaLink) {
    return null;
  }

  // The anchor targets a section of the original destination, so it must not be
  // carried over when the visitor is rerouted to a fallback link.
  const anchorId = extractAnchorIdFromTile(anchorTile);
  const anchorSuffix = !isRestrictedFallback && anchorId ? `#${anchorId}` : '';
  const buttonHref = hasExternalAreaLink ? undefined : `${href}${anchorSuffix}`;

  const showPrice = shouldShowCtaPrice({
    pricePosition,
    priceVariant,
    hasExternalLink: Boolean(externalLink),
    isRestrictedFallback,
  });

  const button = (
    <Button
      className={twJoin(
        (!showPrice || applyClassesToButton) && className,
        addPlayIcon && 'flex items-center gap-2',
        noUppercase && 'normal-case',
      )}
      variant={variant}
      href={buttonHref}
      openInNewTab={hasExternalAreaLink ? undefined : openInNewTab}
      onClick={onClick}
      aria-label={ariaLabel}
      data-tile-element-id={tileElementTrackingId}
    >
      {addPlayIcon && <PlayIcon className="h-5 w-5" />}
      {text}
      {children}
    </Button>
  );

  if (!showPrice) {
    return (
      <>
        {button}
        {modal}
      </>
    );
  }

  const price = <CtaPrice priceVariant={priceVariant} formatCurrency={formatCurrency} />;

  // Same layout as the SKU button, so a linked product and a linked SKU look identical. Like
  // there, `className` styles the wrapper unless the caller asks for it on the button.
  return (
    <div className={twMerge('flex w-fit items-center gap-4', !applyClassesToButton && className)}>
      {pricePosition === 'left' && price}
      {button}
      {pricePosition === 'right' && price}
      {modal}
    </div>
  );
}
